import { useState, useCallback } from 'react'
import { calculateRoute } from '../utils/routeOptimizer.js'

/**
 * Hook for route calculation and step-by-step navigation state.
 */
export function useRoute() {
  const [route, setRoute] = useState(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [calculating, setCalculating] = useState(false)

  /**
   * Calculate the optimal route through the store.
   *
   * @param {string[][]} grid — store grid
   * @param {Array} zones — zone definitions
   * @param {Array} items — shopping list items
   */
  const computeRoute = useCallback((grid, zones, items) => {
    if (!grid || !items || items.length === 0) {
      setRoute(null)
      return
    }

    setCalculating(true)

    // Run in a microtask to keep UI responsive
    Promise.resolve().then(() => {
      try {
        const result = calculateRoute(grid, zones, items)
        setRoute(result)
        setCurrentStep(0)
      } catch (err) {
        console.error('Route calculation failed:', err)
        setRoute(null)
      } finally {
        setCalculating(false)
      }
    })
  }, [])

  /** Advance to the next navigation step */
  const nextStep = useCallback(() => {
    if (!route) return
    setCurrentStep(prev => Math.min(prev + 1, route.stops.length - 1))
  }, [route])

  /** Go back to the previous step */
  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }, [])

  /** Reset navigation to the beginning */
  const resetNavigation = useCallback(() => {
    setCurrentStep(0)
  }, [])

  /** Clear the calculated route */
  const clearRoute = useCallback(() => {
    setRoute(null)
    setCurrentStep(0)
  }, [])

  return {
    route,
    currentStep,
    calculating,
    computeRoute,
    nextStep,
    prevStep,
    resetNavigation,
    clearRoute,
    isNavigating: route !== null,
    isLastStep: route ? currentStep >= route.stops.length - 1 : false,
    progress: route ? { current: currentStep + 1, total: route.stops.length } : null,
  }
}
